import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import type {
  LectureDesignCreate,
  LectureDesignUpdate,
  LectureFromDesignCreate,
  LectureLibraryFieldsUpdate,
  TeachingAidDesignCreateInputs,
  TeachingAidDesignIdResponse,
  TeachingAidDesignLink,
  TeachingAidDesignUpdateInputs,
} from "@/types/contentDesign";
import type { LibraryForkDisplay, LibraryForkInputs } from "@/types/contentV2Versioning";
import { ApiResponse, AzureUploadUrl, IdNamePair, PresentationPage } from "@/types/global";
import {
  BatchPostLectureActivities,
  ChapterLecturePlan,
  ChapterVideoDisplay,
  ChapterVideoRowUpdate,
  LecturePlan,
  LecturePlanRowUpdate,
  LectureSubjectChapterMapInputs,
  LectureVideoMappingDisplay,
  SubjectVideos,
  TeachingAidLibraryDisplay,
  TeachingAidLibraryInputs,
  TeachingAidLibraryUpdateInputs,
  TeachingAidSubjectChapterDisplay,
  TeachingAidSubjectChapterMapInputs,
  TeachingAidSubjectChapterUpdateInputs,
  TeachingLecturePageUpdate,
  UpdateLectureProgressInputs,
  VideoLectureMapInputs,
  VideoSubjectChapterMapInputs,
} from "@/types/teachingContent";

/**
 * content-library (`/v7/content-library`) — the whole content-v2 library surface.
 * All routes per docs/api/content-library.json: the legacy `/teaching-content` prefix
 * was renamed to `/content-library` with resource-style paths (releases/_LATEST.md
 * rename table; api-remediation Phase 1 / AR-0.3), and the former teachingContentApi
 * methods were folded in here alongside the Phase C per-asset-type fork endpoints.
 */
export default class ContentLibraryApi extends ApiVerified {
  async getChapterPlanForBatchSubject(batchId: number, subjectId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterLecturePlan>>(
        `content-library/batches/${batchId}/subjects/${subjectId}/chapter-plans`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLecturePresentationPages(lectureLibraryId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<PresentationPage>>(
        `content-library/lectures/${lectureLibraryId}/presentation-pages`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLecturePresentationPage(
    lecturePageUpdate: TeachingLecturePageUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/lecture-presentation-pages`,
        lecturePageUpdate,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getVideosForCourse(affiliationId: number, courseId: number, showLectureVideos: boolean) {
    try {
      const response = await this.axiosInstance.get<Array<SubjectVideos>>(
        `content-library/courses/${courseId}/videos`,
        {
          params: { affiliationId: affiliationId, showLectureVideos: showLectureVideos },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // onlyActive=true (default) keeps the live plan only; false skips the IsActive
  // check server-side, so deactivated lectures/activities come back with their flag.
  async getLecturePlanForChapter(
    batchId: number,
    subjectChapterId: number,
    onlyActive: boolean = true,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LecturePlan>>(
        `content-library/subject-chapters/${subjectChapterId}/lecture-plan`,
        { params: { batchId: batchId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Map a library lecture onto a chapter — one new LectureSubjectChapter row; returns
  // its id (number). A duplicate active mapping is rejected server-side.
  async mapLectureToSubjectChapter(
    mapInputs: LectureSubjectChapterMapInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `content-library/lecture-subject-chapters`,
        mapInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapVideoToSubjectChapter(mapInputs: VideoSubjectChapterMapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `content-library/video-subject-chapters`,
        mapInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachingVideosForChapter(
    subjectChapterId: number,
    onlyActive: boolean = true,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ChapterVideoDisplay>>(
        `content-library/subject-chapters/${subjectChapterId}/videos`,
        { params: { onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachingLecturesForChapter(subjectChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<IdNamePair>>(
        `content-library/subject-chapters/${subjectChapterId}/lectures`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // subjectChapterId is null for lecture-design source PDFs (no chapter yet) — the
  // backend then keys the blob by a fresh UUID instead of the chapter.
  async getTeachingAidUploadUrl(
    subjectChapterId: number | null,
    teachingAidType: number,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `content-library/teaching-aids/upload-url`,
        {
          params: {
            ...(subjectChapterId !== null ? { subjectChapterId: subjectChapterId } : {}),
            teachingAidType: teachingAidType,
            contentType: contentType,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveLecturePlanForChapter(
    subjectChapterId: number,
    allLectures: Array<LecturePlanRowUpdate>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/subject-chapters/${subjectChapterId}/lecture-plan`,
        {
          SubjectChapterId: subjectChapterId,
          AllLectures: allLectures,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTeachingVideosForChapter(
    subjectChapterId: number,
    allVideos: Array<ChapterVideoRowUpdate>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/subject-chapters/${subjectChapterId}/videos`,
        {
          SubjectChapterId: subjectChapterId,
          AllVideos: allVideos,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getPostLectureActivitiesForCourse(affiliationId: number, courseId: number) {
    try {
      const response = await this.axiosInstance.get<Array<BatchPostLectureActivities>>(
        `content-library/courses/${courseId}/post-lecture-activities`,
        {
          params: { affiliationId: affiliationId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLectureProgress(
    updateLectureProgressInputs: UpdateLectureProgressInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/lecture-progress`,
        updateLectureProgressInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTeachingLectureClicks(
    lectureLibraryId: number,
    pdfClickTypeId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`content-library/lecture-clicks`, {
        LectureLibraryId: lectureLibraryId,
        PdfClickTypeId: pdfClickTypeId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapVideoToLecture(inputs: VideoLectureMapInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content-library/lecture-video-mappings`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachingLectureVideosForChapter(
    affiliationTypeId: number,
    subjectChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LectureVideoMappingDisplay>>(
        `content-library/subject-chapters/${subjectChapterId}/lecture-videos`,
        {
          params: { affiliationTypeId: affiliationTypeId, onlyActive: onlyActive },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Consolidated post-lecture activity (replaces the legacy update/delete
  // teaching-assignment/-assessment family — AR-0.3; those routes 404 now).
  // Body confirmed (backend-answers Q3): update takes ONLY { PostLectureActivityId, ActivityId }.
  async addPostLectureActivity(
    lectureSubjectChapterId: number,
    activityId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `content-library/post-lecture-activities`,
        { LectureSubjectChapterId: lectureSubjectChapterId, ActivityId: activityId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updatePostLectureActivity(
    postLectureActivityId: number,
    activityId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/post-lecture-activities`,
        { PostLectureActivityId: postLectureActivityId, ActivityId: activityId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deletePostLectureActivity(postLectureActivityId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `content-library/post-lecture-activities/${postLectureActivityId}`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Content-v2 lecture design (plan: content-v2-two-step-design) ───
  // The full doc is read via contentReadApi.getLectureDesignDetail and edited in place
  // via updateLectureDesign below (full-doc PUT, contentBaseApi sibling pattern);
  // per-page edits on a library lecture keep using updateLecturePresentationPage.

  // Full-doc replace of the design's own fields (pages/pdf-urls/tags) — PUT
  // lecture-designs/{id}; the body's LectureDesignId mirrors the path param.
  async updateLectureDesign(lectureDesignUpdate: LectureDesignUpdate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/lecture-designs/${lectureDesignUpdate.LectureDesignId}`,
        lectureDesignUpdate,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Create the lecture design doc from its source PDF: the backend splits
  // LecturePresentationPdfUrl into page images itself (no PresentationPages on the
  // wire) and returns the raw 24-char hex LectureDesignId (string).
  async createLectureDesign(lectureDesign: LectureDesignCreate): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `content-library/lecture-designs`,
        lectureDesign,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Edit of the lecture's own fields (name/level/focus) on its LectureLibrary row —
  // the design linkage and chapter mappings are untouched. Returns boolean.
  async updateLectureFields(
    lectureLibraryId: number,
    lectureFieldsUpdate: LectureLibraryFieldsUpdate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/lecture-libraries/${lectureLibraryId}`,
        lectureFieldsUpdate,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // "Use existing design": creates only the LectureLibrary row linked to the given
  // design doc; returns the new LectureLibraryId (number).
  async createLectureFromDesign(
    lectureDesignId: string,
    lectureFromDesign: LectureFromDesignCreate,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<number>(
        `content-library/lecture-designs/${lectureDesignId}/lectures`,
        lectureFromDesign,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // SAS for a design page-image upload. The only documented upload-url path — keyed by
  // lectureDesignId (content-library.json `/lecture-designs/{lectureDesignId}/…`).
  async getLectureDesignPresentationPageUploadUrl(
    lectureDesignId: string,
    pageIndex: number,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `content-library/lecture-designs/${lectureDesignId}/presentation-page-upload-url`,
        {
          params: { pageIndex: pageIndex, contentType: contentType },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Content-v2 TeachingAid split (plan: content-v2-teaching-aid-split, 3 of 4) ───
  // Replaces the legacy single-call all-PDFs get/save teaching-aid flow (removed in taid-3-3).
  // The PDF blob is uploaded to Azure via getTeachingAidUploadUrl (now keyed by subjectChapterId)
  // BEFORE create (BR-5).
  // Per-type add sequence (BR-2):
  //   create-design → save-library → map-to-subject-chapter.
  // Returns: create-design → TeachingAidDesignIdResponse; save-library →
  // TeachingAidLibraryDisplay; map → TeachingAidSubjectChapterDisplay;
  // by-subject-chapter → Array<TeachingAidLibraryDisplay> (1–3 rows);
  // link / update-design / update-library → boolean (re-read by-subject-chapter — OQ-3).

  async createTeachingAidDesign(design: TeachingAidDesignCreateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<TeachingAidDesignIdResponse>(
        `content-library/teaching-aid-designs`,
        design,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTeachingAidDesign(design: TeachingAidDesignUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/teaching-aid-designs`,
        design,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async linkTeachingAidDesign(link: TeachingAidDesignLink): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/teaching-aid-designs/link`,
        link,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveTeachingAidLibrary(library: TeachingAidLibraryInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<TeachingAidLibraryDisplay>(
        `content-library/teaching-aids`,
        library,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTeachingAidLibrary(library: TeachingAidLibraryUpdateInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/teaching-aids`,
        library,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async mapTeachingAidToSubjectChapter(
    mapping: TeachingAidSubjectChapterMapInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<TeachingAidSubjectChapterDisplay>(
        `content-library/teaching-aid-subject-chapter-mappings`,
        mapping,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateTeachingAidChapterMapping(
    mapping: TeachingAidSubjectChapterUpdateInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `content-library/teaching-aid-subject-chapter-mappings`,
        mapping,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeachingAidsForSubjectChapter(subjectChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<TeachingAidLibraryDisplay>>(
        `content-library/subject-chapters/${subjectChapterId}/teaching-aids`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Per-asset-type fork endpoints (Phase C: split from the single legacy
  // `content/library/{assetType}/{id}/fork` — plans/api-remediation/_agent/phase-c-migration-map.md).
  async forkLecture(libraryId: number, inputs: LibraryForkInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<LibraryForkDisplay>(
        `content-library/lectures/${libraryId}/fork`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkVideo(libraryId: number, inputs: LibraryForkInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<LibraryForkDisplay>(
        `content-library/videos/${libraryId}/fork`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async forkTeachingAid(libraryId: number, inputs: LibraryForkInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<LibraryForkDisplay>(
        `content-library/teaching-aids/${libraryId}/fork`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
